import { useMemo } from 'react';
import {
  Badge,
  Button,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FiCopy, FiDownload, FiEdit2, FiGitBranch, FiLayers, FiPlus, FiTag, FiTrash2, FiUpload } from 'react-icons/fi';
import { MdChevronLeft, MdChevronRight, MdSearch } from 'react-icons/md';
import { ColumnDef } from '@tanstack/react-table';

import { PageContainer } from '@/client/components/layout/PageContainer';
import { DataTable } from '@/design-system/components/DataTable';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { RowModal } from '../components/RowModal';
import { ImportModal } from '../components/ImportModal';
import { FieldSchemaManager } from '../components/FieldSchemaManager';
import { CategoryManager } from '../components/CategoryManager';
import { InlineTableEditor } from '../components/InlineTableEditor';
import { useMasterDataPage } from '../hooks';
import type { MasterDataRow } from '@/lib/sdk/master-data/types';

function formatFields(fields: Record<string, unknown>): string {
  return Object.entries(fields).map(([k, v]) => `${k}: ${v}`).join(', ');
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function MasterDataPage() {
  const p = useMasterDataPage();

  const columns = useMemo<ColumnDef<MasterDataRow>[]>(
    () => [
      {
        header: 'Category',
        accessorKey: 'dataset',
        cell: (info) => <Badge colorScheme="purple" textTransform="none">{info.getValue() as string}</Badge>,
        size: 120,
      },
      ...(p.columnConfig.show_code ? [{
        header: 'Code',
        accessorKey: 'key',
        cell: (info) => <Text fontFamily="mono" fontSize="sm">{info.getValue() as string}</Text>,
        size: 140,
      } as ColumnDef<MasterDataRow>] : []),
      {
        header: p.labelColumnName,
        accessorKey: 'label',
        size: 180,
      },
      ...(p.columnConfig.show_parent_key ? [{
        header: 'Belongs To',
        accessorKey: 'parent_key',
        cell: (info) => (
          <Text fontFamily="mono" fontSize="sm" color="text-secondary">
            {(info.getValue() as string | null) || '—'}
          </Text>
        ),
        size: 140,
      } as ColumnDef<MasterDataRow>] : []),
      ...(p.columnConfig.show_additional_info ? [{
        header: 'Additional Info',
        accessorKey: 'fields',
        cell: (info) => (
          <Text fontSize="xs" color="text-secondary" noOfLines={2} maxW="260px">
            {formatFields(info.getValue() as Record<string, unknown>) || '—'}
          </Text>
        ),
        size: 260,
      } as ColumnDef<MasterDataRow>] : []),
      {
        header: 'Created',
        accessorKey: 'created_at',
        cell: (info) => <Text fontSize="xs" color="text-secondary" whiteSpace="nowrap">{formatDate(info.getValue() as string)}</Text>,
        size: 110,
      },
      {
        header: 'Updated',
        accessorKey: 'updated_at',
        cell: (info) => <Text fontSize="xs" color="text-secondary" whiteSpace="nowrap">{formatDate(info.getValue() as string)}</Text>,
        size: 110,
      },
      {
        header: '',
        id: 'actions',
        cell: (info) => (
          <HStack spacing={1} justify="flex-end">
            {p.canAdd && (
              <IconButton
                aria-label="Clone item"
                icon={<FiCopy />}
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); p.handleClone(info.row.original); }}
                data-testid={`md-row-clone-btn-${info.row.original.id}`}
              />
            )}
            {p.canForkRow(info.row.original) && (
              <IconButton
                aria-label="Fork item"
                icon={<FiGitBranch />}
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); p.handleOpenFork(info.row.original); }}
                data-testid={`md-row-fork-btn-${info.row.original.id}`}
              />
            )}
            {p.canEdit && (
              <IconButton
                aria-label="Edit item"
                icon={<FiEdit2 />}
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); p.handleOpenEdit(info.row.original); }}
                data-testid={`md-row-edit-btn-${info.row.original.id}`}
              />
            )}
            {p.canDelete && (
              <IconButton
                aria-label="Delete item"
                icon={<FiTrash2 />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={(e) => { e.stopPropagation(); p.setDeletingRow(info.row.original); }}
                data-testid={`md-row-delete-btn-${info.row.original.id}`}
              />
            )}
          </HStack>
        ),
        size: 170,
      },
    ],
    [p.canAdd, p.canEdit, p.canDelete, p.labelColumnName, p.columnConfig, p.canForkRow, p.handleClone, p.handleOpenFork]
  );

  const selectionToolbarActions = useMemo(() => [
    ...(p.canEdit ? [{ icon: <FiEdit2 />, label: 'Edit Selected', onClick: p.handleEditSelected }] : []),
    ...(p.canDelete ? [{ icon: <FiTrash2 />, label: 'Delete Selected', onClick: p.openDeleteSelected }] : []),
  ], [p.canEdit, p.canDelete, p.handleEditSelected, p.openDeleteSelected]);

  return (
    <PageContainer title="Master Data">
      <VStack spacing={4} align="stretch">
        <HStack spacing={3} flexWrap="wrap" justify="space-between">
          <HStack spacing={3} flexWrap="wrap">
            <Select
              value={p.selectedDataset}
              onChange={(e) => p.handleDatasetChange(e.target.value)}
              size="sm" w="180px" bg="bg-surface" borderColor="border-default"
              data-testid="md-dataset-select"
            >
              <option value="">All categories</option>
              {(p.datasets ?? []).map((d) => <option key={d} value={d}>{d}</option>)}
            </Select>
            <InputGroup size="sm" w="220px">
              <InputLeftElement pointerEvents="none">
                <MdSearch color="var(--chakra-colors-text-muted)" />
              </InputLeftElement>
              <Input
                value={p.searchInput}
                onChange={(e) => p.handleSearchChange(e.target.value)}
                placeholder="Search…"
                bg="bg-surface" borderColor="border-default"
                _hover={{ borderColor: 'border-hover' }}
                _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                data-testid="md-search-input"
              />
            </InputGroup>
            <Input
              type="date" value={p.fromDate} onChange={(e) => p.handleFromChange(e.target.value)}
              size="sm" w="145px" bg="bg-surface" borderColor="border-default"
              _hover={{ borderColor: 'border-hover' }}
              _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
            />
            <Input
              type="date" value={p.toDate} onChange={(e) => p.handleToChange(e.target.value)}
              size="sm" w="145px" bg="bg-surface" borderColor="border-default"
              _hover={{ borderColor: 'border-hover' }}
              _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
            />
            <Text fontSize="sm" color="text-secondary">{p.total} item{p.total !== 1 ? 's' : ''}</Text>
          </HStack>
          <HStack spacing={2}>
            {p.canManageCategories && (
              <Button size="sm" leftIcon={<FiTag />} variant="outline" onClick={() => p.setIsCategoryManagerOpen(true)} data-testid="md-categories-btn">
                Categories
              </Button>
            )}
            {p.selectedDataset && p.canManageFields && (
              <Button size="sm" leftIcon={<FiLayers />} variant="outline" onClick={() => p.setIsFieldSchemaOpen(true)} data-testid="md-manage-fields-btn">
                Manage Fields
              </Button>
            )}
            {p.canExport && (
              <Button size="sm" leftIcon={<FiDownload />} variant="outline" onClick={p.handleExport} isLoading={p.isExporting} data-testid="md-export-btn">
                Export
              </Button>
            )}
            {p.canImport && (
              <Button size="sm" leftIcon={<FiUpload />} variant="outline" onClick={() => { p.setImportResult(null); p.setIsImportOpen(true); }} data-testid="md-import-btn">
                Import
              </Button>
            )}
            {p.canAdd && (
              <Button size="sm" leftIcon={<FiLayers />} variant="outline" onClick={p.handleAddMultiple} data-testid="md-add-multiple-btn">
                Add Multiple
              </Button>
            )}
            {p.canAdd && (
              <Button size="sm" leftIcon={<FiPlus />} colorScheme="brand" onClick={p.handleOpenAdd} data-testid="md-add-item-btn">
                Add Item
              </Button>
            )}
          </HStack>
        </HStack>

        <DataTable
          data={p.rows}
          columns={columns}
          isLoading={p.isLoading}
          emptyMessage="No master data items found."
          enableSorting={false}
          enablePagination={false}
          enableColumnVisibility={true}
          enableSelection={true}
          selectedRows={p.selectedRowIds}
          stickyHeader={true}
          maxTableHeight="600px"
          getRowId={(row) => String(row.id)}
          onRowSelectionChange={({ rowId, checked }) => {
            p.setSelectedRowIds((prev) => checked ? [...prev, rowId] : prev.filter((id) => id !== rowId));
          }}
          onSelectAllChange={({ checked, affectedRows }) => {
            p.setSelectedRowIds(checked ? affectedRows.map((r) => String(r.id)) : []);
          }}
          selectionToolbarActions={selectionToolbarActions}
        />

        <HStack justify="flex-end" spacing={2}>
          <Text fontSize="sm" color="text-secondary">Page {p.page} of {p.pageCount}</Text>
          <IconButton
            aria-label="Previous page" icon={<MdChevronLeft />} size="sm"
            onClick={() => p.setPage((prev) => Math.max(1, prev - 1))}
            isDisabled={p.page <= 1}
          />
          <IconButton
            aria-label="Next page" icon={<MdChevronRight />} size="sm"
            onClick={() => p.setPage((prev) => Math.min(p.pageCount, prev + 1))}
            isDisabled={p.page >= p.pageCount}
          />
        </HStack>
      </VStack>

      <RowModal
        isOpen={p.isRowModalOpen}
        onClose={() => p.setIsRowModalOpen(false)}
        editRow={p.editingRow}
        prefill={p.forkPrefill}
        defaultDataset={p.selectedDataset}
        onSubmit={p.handleRowSubmit}
        isLoading={p.isCreating || p.isUpdating}
        orgId={p.orgId}
        existingDatasets={p.datasets ?? []}
        columnConfig={p.columnConfig}
        labelColumnName={p.labelColumnName}
      />

      <ConfirmDialog
        isOpen={!!p.deletingRow}
        onClose={() => p.setDeletingRow(null)}
        onConfirm={p.handleDeleteConfirm}
        title="Delete Item"
        message={`Delete "${p.deletingRow?.label}"? This cannot be undone.`}
        confirmText="Delete"
        colorScheme="red"
        isLoading={p.isDeleting}
      />

      <ConfirmDialog
        isOpen={p.deletingSelected}
        onClose={() => p.setDeletingSelected(false)}
        onConfirm={p.handleDeleteSelected}
        title="Delete Selected"
        message={`Delete ${p.selectedRowIds.length} selected item${p.selectedRowIds.length !== 1 ? 's' : ''} from "${p.selectedDataset || 'all categories'}"? This cannot be undone.`}
        confirmText="Delete"
        colorScheme="red"
        isLoading={false}
      />

      <ImportModal
        isOpen={p.isImportOpen}
        onClose={() => { p.setIsImportOpen(false); p.setImportResult(null); }}
        defaultDataset={p.selectedDataset}
        onImport={p.handleImport}
        isLoading={p.isImporting}
        result={p.importResult}
        existingDatasets={p.datasets ?? []}
      />

      <CategoryManager
        isOpen={p.isCategoryManagerOpen}
        onClose={() => p.setIsCategoryManagerOpen(false)}
        orgId={p.orgId}
      />

      {p.selectedDataset && (
        <FieldSchemaManager
          isOpen={p.isFieldSchemaOpen}
          onClose={() => p.setIsFieldSchemaOpen(false)}
          dataset={p.selectedDataset}
          orgId={p.orgId}
        />
      )}

      <InlineTableEditor
        isOpen={p.isBulkEditorOpen}
        onClose={() => p.setIsBulkEditorOpen(false)}
        onSaved={() => { p.setSelectedRowIds([]); p.refetch(); }}
        dataset={p.selectedDataset || (p.datasets?.[0] ?? '')}
        orgId={p.orgId}
        fieldDefs={p.fieldDefs}
        initialRows={p.bulkEditorRows}
        mode={p.bulkEditorMode}
        labelColumnName={p.labelColumnName}
        columnConfig={p.columnConfig}
      />
    </PageContainer>
  );
}
